'use memo';

import {
  AgxAvatar,
  AgxAlerts,
  AgxBadge,
  AgxButton,
  AgxDivider,
  AgxEmptyState,
  AgxModal,
  AgxRadioGroup,
  AgxSelectChip,
  AgxSpacer,
  AgxTab,
  AgxTable,
  AgxTypography,
  AutopilotMarkLogo,
  type RowData,
  type SelectChipOption,
  useToast,
} from '@urbanx/agx.ui-components2';
import LoadingState from 'components/loadingState/LoadingState';
import { ContentLayout, PageLayout } from 'layout';
import {
  AgenciesApiException,
  type FeatureConfigurationResponse,
  type FeatureConfigurationScope,
  FeatureConfigurationScopeLevel,
  FeatureConfigurationScopeState,
  type FeatureDefinition,
  LicenceDetailsState,
  useGetAllAgencies,
  useInvalidateUseStaffGetFeatureToggleManagement,
  useStaffGetFeatureToggleManagement,
  useStaffUpdateFeatureSetting,
} from '@urbanx/agx.agencies.client';
import { Fragment, useState } from 'react';

import {
  type AgencyScope,
  type FeatureChoice,
  type FeatureKey,
  type FeatureLevel,
  type FeatureState,
  appliesTo,
  choiceOptions,
  cloneConfiguration,
  concreteChoiceOptions,
  getAgencyOptions,
  getAgencyStates,
  type DescendantOverridePreviewItem,
  getDescendantOverridePreview,
  getFeatureChoice,
  getSelectedAgency,
  isPlatformFeature,
  levelTabs,
  resolveAgencyFeature,
  resolveAgencyWideFeature,
  resolveStateFeature,
  resetDescendantOverrides,
  setFeatureChoice,
  stateOptions,
  tableColumns,
} from './helpers';
import './FeatureToggles.scss';

interface PendingChange {
  configuration: FeatureConfigurationResponse;
  feature: FeatureKey;
  featureName: string;
  choice: FeatureChoice;
  value: boolean | undefined;
  overrides: DescendantOverridePreviewItem[];
}

type DescendantAction = 'preserve' | 'reset';

const FeatureToggles = () => {
  const { pushToast } = useToast();
  const [selectedLevel, setSelectedLevel] = useState<FeatureLevel>('global');
  const [selectedState, setSelectedState] = useState<FeatureState>(LicenceDetailsState.QLD);
  const [selectedAgencyId, setSelectedAgencyId] = useState('');
  const [agencyScope, setAgencyScope] = useState<AgencyScope>('all');
  const [optimisticConfiguration, setOptimisticConfiguration] =
    useState<FeatureConfigurationResponse>();
  const [pendingChange, setPendingChange] = useState<PendingChange>();
  const [descendantAction, setDescendantAction] = useState<DescendantAction>('preserve');
  const [savingFeature, setSavingFeature] = useState<FeatureKey>();

  const {
    data: management,
    isPending: isLoadingFeatures,
    isError: featuresFailed,
  } = useStaffGetFeatureToggleManagement();
  const {
    data: agencies,
    isPending: isLoadingAgencies,
    isError: agenciesFailed,
  } = useGetAllAgencies();
  const invalidateFeatureToggleManagement = useInvalidateUseStaffGetFeatureToggleManagement();

  const draft = optimisticConfiguration ?? cloneConfiguration(management?.configuration);
  const selectedAgency = getSelectedAgency(agencies, selectedAgencyId);
  const agencyStates = getAgencyStates(selectedAgency);
  const effectiveAgencyScope = agencyStates.length === 1 ? agencyStates[0] : agencyScope;
  const features = (management?.features ?? []).filter(
    (feature): feature is FeatureDefinition & { feature: FeatureKey } => !!feature.feature
  );
  const isLoading = isLoadingFeatures || isLoadingAgencies;
  const isReady =
    !isLoading && !featuresFailed && !agenciesFailed && !!management?.eTag && !!agencies;
  const scopeComplete =
    isReady &&
    (selectedLevel !== 'agency' ||
      (!!selectedAgencyId &&
        (effectiveAgencyScope === 'all' || agencyStates.includes(effectiveAgencyScope))));

  const {
    staffUpdateFeatureSetting: saveFeatureSetting,
    staffUpdateFeatureSettingPending: isSaving,
  } = useStaffUpdateFeatureSetting({
    onSuccess: async () => {
      await invalidateFeatureToggleManagement();
      setOptimisticConfiguration(undefined);
      setSavingFeature(undefined);
    },
    onError: async (error) => {
      await invalidateFeatureToggleManagement();
      setOptimisticConfiguration(undefined);
      setSavingFeature(undefined);
      if (AgenciesApiException.isAgenciesApiException(error) && error.status === 409) {
        pushToast({
          message:
            'Feature configuration changed while you were editing. Review the latest values.',
          type: 'error',
        });
        return;
      }
      pushToast({ message: 'Error saving feature configuration', type: 'error' });
    },
  });

  const scope: FeatureConfigurationScope =
    selectedLevel === 'global'
      ? { level: FeatureConfigurationScopeLevel.Global }
      : selectedLevel === 'state'
        ? {
            level: FeatureConfigurationScopeLevel.State,
            state: FeatureConfigurationScopeState[selectedState],
          }
        : effectiveAgencyScope === 'all'
          ? {
              level: FeatureConfigurationScopeLevel.Agency,
              agencyId: selectedAgencyId,
            }
          : {
              level: FeatureConfigurationScopeLevel.AgencyState,
              state: FeatureConfigurationScopeState[effectiveAgencyScope],
              agencyId: selectedAgencyId,
            };

  const applyChange = (change: PendingChange, resetDescendantsToInherit = false) => {
    if (!management?.eTag) return;

    const configuration = resetDescendantsToInherit
      ? resetDescendantOverrides(
          change.configuration,
          agencies ?? [],
          selectedLevel,
          change.feature,
          selectedState,
          selectedAgencyId,
          effectiveAgencyScope
        )
      : change.configuration;
    setPendingChange(undefined);
    setOptimisticConfiguration(configuration);
    setSavingFeature(change.feature);
    void saveFeatureSetting({
      scope,
      feature: change.feature,
      value: change.value,
      eTag: management.eTag,
      resetDescendantsToInherit,
    }).catch(() => undefined);
  };

  const changeFeature = (
    definition: FeatureDefinition & { feature: FeatureKey },
    choice: FeatureChoice
  ) => {
    if (!scopeComplete || isSaving || savingFeature || pendingChange) return;
    const configuration = setFeatureChoice(
      draft,
      selectedLevel,
      definition.feature,
      choice,
      isPlatformFeature(definition),
      selectedState,
      selectedAgencyId,
      effectiveAgencyScope
    );
    const overrides = getDescendantOverridePreview(
      draft,
      agencies ?? [],
      selectedLevel,
      definition.feature,
      isPlatformFeature(definition),
      selectedState,
      selectedAgencyId,
      effectiveAgencyScope
    );
    const change: PendingChange = {
      configuration,
      feature: definition.feature,
      featureName: definition.name ?? definition.feature,
      choice,
      value: choice === 'inherit' ? undefined : choice === 'on',
      overrides,
    };

    if (overrides.length > 0) {
      setDescendantAction('preserve');
      setPendingChange(change);
      return;
    }

    applyChange(change);
  };

  const controlsLocked = isSaving || !!savingFeature || !!pendingChange;

  const resolvedFeature = (definition: FeatureDefinition & { feature: FeatureKey }) => {
    const platformFeature = isPlatformFeature(definition);

    if (selectedLevel === 'global') {
      const enabled = draft.global?.[definition.feature] === true;
      return {
        value: enabled,
        source: enabled ? 'Global' : 'Default off',
        sourceLevel: 'global' as const,
      };
    }

    if (selectedLevel === 'state') {
      return resolveStateFeature(draft, selectedState, definition.feature, platformFeature);
    }

    if (effectiveAgencyScope === 'all') {
      return resolveAgencyWideFeature(draft, selectedAgencyId, agencyStates, definition.feature);
    }

    return resolveAgencyFeature(
      draft,
      selectedAgencyId,
      effectiveAgencyScope,
      definition.feature,
      platformFeature
    );
  };

  const getRows = (definitions: (FeatureDefinition & { feature: FeatureKey })[]): RowData[] =>
    definitions.map((definition) => {
      const platformFeature = isPlatformFeature(definition);
      const concrete =
        selectedLevel === 'global' || (selectedLevel === 'state' && !platformFeature);
      const storedChoice = getFeatureChoice(
        draft,
        selectedLevel,
        definition.feature,
        selectedState,
        selectedAgencyId,
        effectiveAgencyScope
      );
      const choice = concrete && storedChoice === 'inherit' ? 'off' : storedChoice;
      const resolved = resolvedFeature(definition);
      const mixed = resolved.source.startsWith('Mixed:');
      const inheritedFrom = {
        global: 'Global',
        state: 'State',
        agency: 'Agency',
        agencyState: 'Agency state',
      }[resolved.sourceLevel];

      return {
        dataTestId: `featureToggle-row-${definition.feature}`,
        Feature: (
          <AgxSpacer direction="vertical" gap="spacing-xxs" fill>
            <AgxTypography id={`feature-${definition.feature}`} type="body_ui_strong" size="small">
              {definition.name}
            </AgxTypography>
            <AgxTypography
              id={`feature-description-${definition.feature}`}
              type="caption"
              size="medium"
              color="var(--text-tertiary)"
            >
              {definition.description}
            </AgxTypography>
          </AgxSpacer>
        ),
        Status: (
          <AgxSpacer direction="horizontal" contentAlignment="center" gap="spacing-xs">
            <AgxBadge
              type="colourfill"
              variant="normal"
              size="md"
              text={mixed ? 'Varies by state' : resolved.value ? 'Live' : 'Offline'}
              colour={mixed ? 'yellow' : resolved.value ? 'green' : 'lightgrey'}
            />
            {choice === 'inherit' && (
              <AgxTypography
                id={`feature-status-source-${definition.feature}`}
                type="caption"
                size="medium"
                color="var(--text-tertiary)"
              >
                — {inheritedFrom}
              </AgxTypography>
            )}
            {savingFeature === definition.feature && (
              <AgxTypography
                id={`feature-saving-${definition.feature}`}
                type="caption"
                size="medium"
                color="var(--text-tertiary)"
              >
                Saving…
              </AgxTypography>
            )}
          </AgxSpacer>
        ),
        'Set to': (
          <div
            className={concrete ? 'featureTogglesPage__setting--concrete' : undefined}
            role="radiogroup"
            aria-labelledby={`feature-${definition.feature}`}
          >
            <AgxRadioGroup
              key={`${definition.feature}-${choice}-${pendingChange?.feature === definition.feature ? 'pending' : 'settled'}`}
              id={`featureToggle-${definition.feature}`}
              layout="horizontal"
              options={concrete ? concreteChoiceOptions : choiceOptions}
              value={choice}
              onChange={(value) => changeFeature(definition, value as FeatureChoice)}
              disabled={!scopeComplete || controlsLocked}
            />
          </div>
        ),
      };
    });

  const visibleFeatures = features
    .filter((definition) => {
      if (selectedLevel === 'global') return isPlatformFeature(definition);
      if (selectedLevel === 'agency') {
        return effectiveAgencyScope === 'all'
          ? isPlatformFeature(definition)
          : appliesTo(definition, effectiveAgencyScope);
      }
      return appliesTo(definition, selectedState);
    })
    .sort((left, right) => (left.name ?? left.feature).localeCompare(right.name ?? right.feature));
  const platformFeatures = visibleFeatures.filter(isPlatformFeature);
  const stateFeatures = visibleFeatures.filter((feature) => !isPlatformFeature(feature));
  const agencyScopeOptions =
    agencyStates.length === 1
      ? agencyStates.map((state) => ({ label: state, value: state }))
      : [
          { label: 'All states', value: 'all' },
          ...agencyStates.map((state) => ({ label: state, value: state })),
        ];

  const onLevelChange = (value: string) => {
    setSelectedLevel(value as FeatureLevel);
    setAgencyScope('all');
    if (value !== 'agency') setSelectedAgencyId('');
  };

  const firstSelected = (options: SelectChipOption[]) => options[0]?.value;

  const featureSection = (
    title: string,
    description: string,
    definitions: (FeatureDefinition & { feature: FeatureKey })[]
  ) =>
    definitions.length > 0 && (
      <section className="featureTogglesPage__section">
        <AgxSpacer direction="vertical" gap="spacing-3xl" fill>
          <AgxSpacer direction="vertical" gap="spacing-md" fill>
            <AgxTypography
              id={`${title}-heading`}
              type="heading"
              size="x-small"
              color="var(--text-primary)"
            >
              {title}
            </AgxTypography>
            <AgxTypography
              id={`${title}-description`}
              type="body"
              size="small"
              color="var(--text-tertiary)"
            >
              {description}
            </AgxTypography>
          </AgxSpacer>
          <AgxTable
            columns={tableColumns}
            rows={getRows(definitions)}
            showLoadingState={isLoading}
            tableStyle="square"
          />
        </AgxSpacer>
      </section>
    );

  const toolbar = (
    <div className="featureTogglesPage__toolbar">
      <div
        aria-disabled={controlsLocked}
        style={{ pointerEvents: controlsLocked ? 'none' : undefined }}
      >
        <AgxTab
          options={levelTabs.map((tab) => ({ ...tab, selected: tab.id === selectedLevel }))}
          onChange={(tab) => !controlsLocked && onLevelChange(tab.id)}
        />
      </div>

      <div className="featureTogglesPage__toolbarControls">
        {selectedLevel === 'state' && (
          <AgxSelectChip
            id="featureToggleState"
            label="State"
            variant="chip"
            size="medium"
            type="single-select"
            options={stateOptions}
            defaultSelection={selectedState}
            disabled={controlsLocked}
            onSelect={(options) => {
              const value = firstSelected(options);
              if (value) setSelectedState(value as FeatureState);
            }}
            hideFooter
          />
        )}

        {selectedLevel === 'agency' && (
          <>
            <AgxSelectChip
              id="featureToggleAgency"
              dataTestId="featureToggleAgency-selected"
              label="Agency"
              variant="chip"
              size="medium"
              type="single-select"
              options={getAgencyOptions(agencies)}
              defaultSelection={selectedAgencyId}
              disabled={controlsLocked}
              searchPlaceHolder="Search agencies"
              onSelect={(options) => {
                const value = firstSelected(options);
                if (!value) return;
                setSelectedAgencyId(value);
                setAgencyScope('all');
              }}
              hideFooter
            />
            <AgxSelectChip
              id="featureToggleAgencyScope"
              label="In state"
              variant="chip"
              size="medium"
              type="single-select"
              align="right"
              options={agencyScopeOptions}
              defaultSelection={effectiveAgencyScope}
              onSelect={(options) => {
                const value = firstSelected(options);
                if (value) setAgencyScope(value as AgencyScope);
              }}
              disabled={controlsLocked || agencyStates.length === 1}
              hideFooter
            />
          </>
        )}
      </div>
    </div>
  );

  const descendantLabel =
    selectedLevel === 'global'
      ? 'states and agencies'
      : selectedLevel === 'state'
        ? 'agencies'
        : 'states';
  const pendingTitle = pendingChange
    ? pendingChange.choice === 'on'
      ? `You are about to enable the [${pendingChange.featureName}] feature.`
      : pendingChange.choice === 'off'
        ? `You are about to disable the [${pendingChange.featureName}] feature.`
        : `You are about to set the [${pendingChange.featureName}] feature to Inherit.`
    : '';
  const overrideGroups = pendingChange
    ? [
        {
          label: 'States',
          overrides: pendingChange.overrides.filter((override) => override.type === 'state'),
        },
        {
          label: 'Agencies',
          overrides: pendingChange.overrides.filter((override) => override.type === 'agency'),
        },
      ].filter((group) => group.overrides.length > 0)
    : [];

  return (
    <PageLayout
      primaryPageTitle="Feature Toggles"
      agentName=""
      pageIcon={
        <AgxAvatar
          className="featureTogglesPage__headerMark"
          size="lg"
          type="icon-dark"
          icon={<AutopilotMarkLogo width={25} height={18} />}
        />
      }
      header={toolbar}
    >
      <ContentLayout hideBackground>
        <div className="featureTogglesPage">
          {selectedLevel === 'agency' && selectedAgency && (
            <AgxTypography id="selectedAgencyHeading" type="heading" size="large">
              {selectedAgency.name}
            </AgxTypography>
          )}
          {!isLoading && !scopeComplete && (
            <section className="featureTogglesPage__section">
              <AgxEmptyState
                variant="advanced"
                glyphType={featuresFailed || agenciesFailed ? 'ghost' : 'store'}
                labelStyle="bodyUI"
                label={
                  featuresFailed || agenciesFailed
                    ? "We couldn't load these settings. Try refreshing the page."
                    : 'Pick an agency above to see its features.'
                }
                actionButtons={[]}
              />
            </section>
          )}
          {scopeComplete &&
            featureSection(
              `${selectedLevel === 'agency' ? effectiveAgencyScope : selectedState}-only features`,
              selectedLevel === 'agency'
                ? `These features exist in ${effectiveAgencyScope} only. What you set here applies to this agency and nobody else.`
                : `These features exist in ${selectedState} only. What you set here becomes the ${selectedState} default for every agency.`,
              stateFeatures
            )}
          {scopeComplete &&
            featureSection(
              'Features in every state',
              selectedLevel === 'global'
                ? 'These are the defaults for every agency, everywhere. A state or an agency can be given its own setting that wins over what you choose here.'
                : selectedLevel === 'state'
                  ? `What you set here applies in ${selectedState} for every agency. Leave a feature on Inherit to keep following the global setting.`
                  : effectiveAgencyScope === 'all'
                    ? 'What you set here applies to this agency in all of its states. Leave a feature on Inherit to keep following the global setting.'
                    : `What you set here applies to this agency in ${effectiveAgencyScope} only. Leave a feature on Inherit to keep following whatever it follows today.`,
              platformFeatures
            )}
          {isLoading && <LoadingState />}
        </div>
      </ContentLayout>

      {pendingChange && (
        <AgxModal
          id="feature-toggle-impact-confirmation"
          className="featureToggleInheritanceModal"
          title={pendingTitle}
          secondaryTitle={
            <AgxTypography
              id="featureToggleInheritanceSubtitle"
              type="body_ui"
              size="small"
              color="var(--text-secondary)"
            >
              Please confirm your preferred method for applying this change.
            </AgxTypography>
          }
          type="single"
          variant="withDivider"
          maxWidth="600px"
          isOpen
          setIsDialogOpen={(isOpen) => !isOpen && setPendingChange(undefined)}
          actionButtonContainerStyle="withoutDivider"
          actionButtons={
            <AgxSpacer direction="horizontal" gap="spacing-md" contentJustification="center" fill>
              <AgxButton
                id="cancelFeatureToggleSave"
                text="Cancel"
                variant="hollow"
                size="large"
                stretch
                onClick={() => setPendingChange(undefined)}
              />
              <AgxButton
                id="confirmFeatureToggleSave"
                text="Confirm and update"
                variant="primary"
                size="large"
                stretch
                disabled={!isReady}
                onClick={() => applyChange(pendingChange, descendantAction === 'reset')}
              />
            </AgxSpacer>
          }
          content={
            <AgxSpacer
              direction="vertical"
              gap="spacing-3xl"
              className="featureToggleInheritanceModal__content"
            >
              <AgxRadioGroup
                id="featureToggleDescendantAction"
                layout="stacked"
                label="How should this change be applied?"
                required
                options={[
                  {
                    label: 'Update this setting only',
                    subLabel: `Only ${descendantLabel} set to Inherit will receive the new value; existing overrides remain unchanged.`,
                    value: 'preserve',
                  },
                  {
                    label: `Set all ${descendantLabel} to Inherit`,
                    subLabel:
                      'Removes their existing overrides so they receive the value from this setting.',
                    value: 'reset',
                  },
                ]}
                value={descendantAction}
                onChange={(value) => setDescendantAction(value as DescendantAction)}
              />
              <AgxSpacer direction="vertical" gap="spacing-md" fill>
                <AgxAlerts
                  id="featureToggleDescendantOverridesAlert"
                  dismissedAlertIds={[]}
                  hideDismissButton
                  onValueChanged={() => undefined}
                  alerts={[
                    {
                      id: 'feature-toggle-descendant-overrides',
                      alertType: 'Information',
                      isDismissed: false,
                      heading: `These ${pendingChange.overrides.length} ${descendantLabel} are currently not set to “Inherit”.`,
                      description: '',
                      solveElementId: '',
                    },
                  ]}
                />
                <AgxSpacer direction="vertical" gap="spacing-lg" fill>
                  {overrideGroups.map((group) => (
                    <AgxSpacer key={group.label} direction="vertical" gap="spacing-sm" fill>
                      <AgxTypography
                        id={`featureToggleOverrideGroup-${group.label}`}
                        type="body_ui_strong"
                        size="small"
                      >
                        {group.label}
                      </AgxTypography>
                      <div className="featureToggleInheritanceModal__overrides">
                        {group.overrides.map((override, index) => (
                          <Fragment key={override.id}>
                            <AgxSpacer
                              direction="horizontal"
                              contentAlignment="center"
                              contentJustification="space-between"
                              padding="spacing-xl"
                              fill
                            >
                              <AgxTypography
                                id={`featureToggleOverride-${override.id}`}
                                type="body_ui"
                                size="small"
                                color="var(--text-tertiary)"
                              >
                                {override.label}
                              </AgxTypography>
                              <AgxTypography
                                id={`featureToggleOverrideValue-${override.id}`}
                                type="body_ui_strong"
                                size="small"
                                color={
                                  override.value === 'On'
                                    ? 'var(--text-success)'
                                    : 'var(--text-tertiary)'
                                }
                              >
                                {override.value}
                              </AgxTypography>
                            </AgxSpacer>
                            {index < group.overrides.length - 1 && (
                              <AgxDivider variant="horizontal" />
                            )}
                          </Fragment>
                        ))}
                      </div>
                    </AgxSpacer>
                  ))}
                </AgxSpacer>
              </AgxSpacer>
            </AgxSpacer>
          }
        />
      )}
    </PageLayout>
  );
};

export default FeatureToggles;
